import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useDialog } from '@/hooks/useDialog';
import { motion } from 'framer-motion';
import { Edit2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// TanStack Query imports
import { useDeleteEntity, useEntity, useUpdateEntity } from '@/services/query/entities-queries';
import { entityTemplateKeys, useEntityTemplateData, useSyncEntityFields, useUpdateEntityFieldValues } from '@/services/query/entity-templates-queries';
import { useAddEntityToStory, useStoryEntities } from '@/services/query/story-entities-queries';
import { useQueryClient } from '@tanstack/react-query';


// Component imports
import EntityFieldsTab from './components/EntityFieldsTab';
import { EntityHeader } from './components/EntityHeader';
import ErrorState from './components/ErrorState';
import LoadingState from './components/LoadingState';
import NotFoundState from './components/NotFoundState';

// Types
import { FormDialog } from '@/components/ui/form-dialog';
import EntityNotesTab from './components/EntityNotesTab';
import { EditEntityDialogData, ExtendedEntity, FormValues, TemplateData } from './types';
import { cn } from '@udecode/cn';

function EntityDetailsPage() {
  const { entityId, storyId } = useParams();
  const { toast } = useToast();
  const { openDialog, registerDialog } = useDialog();

  // State
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [formValues, setFormValues] = useState<FormValues>({});
  const [activeTab, setActiveTab] = useState<'overview' | 'notes'>('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);



  // Initialize queryClient
  const queryClient = useQueryClient();

  // TanStack Query hooks
  const entityQuery = useEntity(entityId);
  const templateDataQuery = useEntityTemplateData(entityId);
  const storyEntitiesQuery = useStoryEntities(storyId);
  const updateEntityMutation = useUpdateEntity();
  const updateFieldValuesMutation = useUpdateEntityFieldValues();
  const syncFieldsMutation = useSyncEntityFields();
  const addEntityToStoryMutation = useAddEntityToStory();
  const deleteEntityMutation = useDeleteEntity();
  const navigate = useNavigate();

  // Derived state with proper type safety
  const entity = entityQuery.data as ExtendedEntity | undefined;
  let templateData = templateDataQuery.data as TemplateData | undefined;

  // Ensure templateData has entity and template properties
  if (templateData && entity) {
    // If templateData doesn't have entity property, add it
    if (!templateData.entity) {
      templateData = {
        ...templateData,
        entity: {
          id: entity.id,
          template_id: entity.template_id,
          name: entity.name
        }
      };
    }

    // If templateData doesn't have template property, add it
    if (!templateData.template && entity.template_id) {
      templateData = {
        ...templateData,
        template: {
          id: entity.template_id,
          name: entity.templates?.name || 'Template'
        }
      };
    }
  }

  const isLoading = entityQuery.isLoading || templateDataQuery.isLoading;
  const error = entityQuery.error?.message || templateDataQuery.error?.message || null;
  const isSaving = updateFieldValuesMutation.isPending;
  const isSyncing = syncFieldsMutation.isPending;

  // Register the FormDialog component for the EditEntityDialog key
  useEffect(() => {
    if (registerDialog) {
      registerDialog('EditEntityDialog', FormDialog);
    } else {
      console.error('registerDialog function is not available');
    }
  }, [registerDialog]);

  // Initialize form values when template data is loaded
  useEffect(() => {
    if (templateData && entityId) {
      const localStorageKey = `plot-studio-entity-fields-${entityId}`;

      // First, try to get saved values from localStorage
      let savedValues: FormValues = {};
      try {
        const savedContent = localStorage.getItem(localStorageKey);
        if (savedContent) {
          savedValues = JSON.parse(savedContent);
          console.log('Loaded entity field values from localStorage:', savedValues);
        }
      } catch (error) {
        console.error('Failed to parse localStorage content for entity fields:', error);
        localStorage.removeItem(localStorageKey);
      }

      const initialValues: FormValues = {};

      // Add values from fields without section
      if (templateData.fieldsWithoutSection && Array.isArray(templateData.fieldsWithoutSection)) {
        templateData.fieldsWithoutSection.forEach((field) => {
          // Skip if field is null or not an object, or doesn't have an id
          if (field && typeof field === 'object' && field.id) {
            // Use localStorage value if available, otherwise use database value
            initialValues[field.id] = savedValues[field.id] !== undefined
              ? savedValues[field.id]
              : (field.value || '');
          }
        });
      }

      // Add values from fields in sections
      if (templateData.sections && Array.isArray(templateData.sections)) {
        templateData.sections.forEach((section) => {
          // Skip if section is null or not an object
          if (!section || typeof section !== 'object') return;

          // Check if template_fields exists and is an array
          if (section.template_fields && Array.isArray(section.template_fields)) {
            section.template_fields.forEach((field) => {
              if (field && field.id) {
                // Use localStorage value if available, otherwise use database value
                initialValues[field.id] = savedValues[field.id] !== undefined
                  ? savedValues[field.id]
                  : (field.value || '');
              }
            });
          }


        });
      }

      setFormValues(initialValues);
    }
  }, [templateData, entityId]);

  // Function to refresh data (used after mutations)
  const refreshData = useCallback(async () => {
    try {
      setIsRefreshing(true);
      if (entityId) {
        // First invalidate the queries to ensure fresh data
        await queryClient.invalidateQueries({
          queryKey: entityTemplateKeys.detail(entityId),
        });
      }

      // Then refetch the data
      await Promise.all([
        entityQuery.refetch(),
        templateDataQuery.refetch(),
        storyId ? storyEntitiesQuery.refetch() : Promise.resolve()
      ]);

      toast({
        title: "Refreshed",
        description: "Page data updated successfully",
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast({
        title: "Error",
        description: "Failed to refresh data",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [entityId, entityQuery, templateDataQuery, storyEntitiesQuery, storyId, queryClient, toast]);

  const handleEditEntity = useCallback(() => {
    if (!entity) return;

    // Prepare dialog data
    const dialogData: EditEntityDialogData = {
      title: "Edit Entity",
      description: `Edit ${entity.templates?.name || 'Entity'} details`,
      fields: [
        { name: "name", label: "Name", type: "text", required: true, defaultValue: entity.name },
        { name: "description", label: "Description", type: "textarea", defaultValue: entity.description },
        {
          name: "status",
          label: "Status",
          type: "select",
          options: [
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
            { value: "draft", label: "Draft" }
          ],
          defaultValue: entity.status,
        }
      ],
      submitText: "Save Changes"
    };

    // Open the dialog
    openDialog("EditEntityDialog", dialogData, async (data: { name: string; description: string | null; status: string }) => {
      try {
        await updateEntityMutation.mutateAsync({
          entityId: entity.id,
          data: {
            name: data.name,
            description: data.description,
            status: data.status
          }
        });

        toast({
          title: "Success",
          description: "Entity updated successfully",
        });

        // Refresh data
        await refreshData();
      } catch (error) {
        console.error('Error updating entity:', error);
        toast({
          title: "Error",
          description: "Failed to update entity",
          variant: "destructive",
        });
      }
    });
  }, [entity, openDialog, updateEntityMutation, toast, refreshData]);

  const handleAddToStory = useCallback(async () => {
    if (!storyId || !entity) return;

    // Check if entity is already in the story
    const existingEntity = storyEntitiesQuery.data?.find(
      (item) => item.entity_id === entity.id
    );

    if (existingEntity) {
      toast({
        title: "Already Added",
        description: "This entity is already part of the story",
      });
      return;
    }

    try {
      // Use the mutation to add entity to story
      await addEntityToStoryMutation.mutateAsync({
        storyId,
        entityId: entity.id,
        role: ''
      });

      toast({
        title: "Success",
        description: "Entity added to story successfully",
      });
    } catch (error) {
      console.error('Error adding entity to story:', error);
      toast({
        title: "Error",
        description: "Failed to add entity to story",
        variant: "destructive",
      });
    }
  }, [storyId, entity, storyEntitiesQuery.data, toast, addEntityToStoryMutation]);

  const handleInputChange = useCallback((fieldId: number, value: string | null) => {
    const updatedValues = {
      ...formValues,
      [fieldId]: value
    };

    setFormValues(updatedValues);

    // Save to localStorage
    if (entityId) {
      const localStorageKey = `plot-studio-entity-fields-${entityId}`;
      try {
        localStorage.setItem(localStorageKey, JSON.stringify(updatedValues));
        console.log('Entity field values saved to localStorage');
      } catch (error) {
        console.error('Failed to save entity field values to localStorage:', error);
      }
    }
  }, [formValues, entityId]);

  const handleSaveChanges = useCallback(async () => {
    if (!entity) return;

    try {
      // Convert ID-based values to Name-based values using templateData
      const fieldValuesByName: Record<string, any> = {};
      const fieldsMap = new Map<number, string>();

      // Build map from template data
      if (templateData) {
        templateData.fieldsWithoutSection?.forEach((f: any) => {
          if (f.id && f.field_name) fieldsMap.set(f.id, f.field_name);
        });
        templateData.sections?.forEach((s: any) => {
          s.template_fields?.forEach((f: any) => {
            if (f.id && f.field_name) fieldsMap.set(f.id, f.field_name);
          });
        });
      }

      // Map values
      Object.entries(formValues).forEach(([idStr, value]) => {
        const id = Number(idStr);
        const name = fieldsMap.get(id);
        if (name) {
          fieldValuesByName[name] = value;
        }
      });

      // Use the mutation to save field values (optimized, no redundant fetching)
      await updateFieldValuesMutation.mutateAsync({
        entityId: entity.id,
        fieldValues: fieldValuesByName
      });

      // Clear localStorage after successful save
      if (entityId) {
        const localStorageKey = `plot-studio-entity-fields-${entityId}`;
        try {
          localStorage.removeItem(localStorageKey);
          console.log('Entity field values cleared from localStorage after successful save');
        } catch (error) {
          console.error('Failed to clear localStorage after save:', error);
        }
      }

      toast({
        title: "Success",
        description: "Changes saved successfully",
      });

      // Switch back to preview mode
      setMode('preview');

      // No need to call refreshData() manually as the mutation invalidates queries
    } catch (error) {
      console.error('Error saving changes:', error);
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
      });
    }
  }, [entity, formValues, updateFieldValuesMutation, toast, setMode, entityId, templateData]);

  // Handle syncing entity fields with template
  const handleSyncFields = useCallback(async () => {
    if (!entity) return;

    try {
      // Call the sync fields mutation
      const result = await syncFieldsMutation.mutateAsync({
        entityId: entity.id
      });

      // Show success message with the number of fields synced
      toast({
        title: "Success",
        description: `Entity fields synced successfully. ${result.fields_synced} new field${result.fields_synced !== 1 ? 's' : ''} added.`,
      });

      // Refresh data
      await refreshData();
    } catch (error) {
      console.error('Error syncing entity fields:', error);
      toast({
        title: "Error",
        description: "Failed to sync entity fields",
        variant: "destructive",
      });
    }
  }, [entity, syncFieldsMutation, toast, refreshData]);

  const handleDeleteEntity = useCallback(async () => {
    if (!entity) return;

    if (confirm(`Are you sure you want to delete ${entity.name}? This action cannot be undone.`)) {
      try {
        await deleteEntityMutation.mutateAsync({
          entityId: entity.id,
          projectId: entity.project_id
        });

        toast({
          title: "Entity Deleted",
          description: `${entity.name} has been deleted.`,
        });
        // Navigate back to overview or list
        navigate(-1);
      } catch (error) {
        console.error('Error deleting entity:', error);
        toast({
          title: "Error",
          description: "Failed to delete entity",
          variant: "destructive"
        });
      }
    }
  }, [entity, deleteEntityMutation, navigate, toast]);







  // Handle retry loading
  const handleRetry = useCallback(async () => {
    try {
      await Promise.all([
        entityQuery.refetch(),
        templateDataQuery.refetch()
      ]);
    } catch (error) {
      console.error('Error retrying data fetch:', error);
    }
  }, [entityQuery, templateDataQuery]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={handleRetry} />;
  }

  if ((!isLoading && !entity) || (!isLoading && !templateData)) {
    return <NotFoundState />;
  }

  return (
    <div className="min-h-screen bg-transparent text-foreground flex flex-col font-sans">
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-10">

        {/* Header Section */}
        <div className="flex flex-col gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <EntityHeader
              entity={entity}
              onEdit={handleEditEntity}
              onSync={handleSyncFields}
              onAddToStory={handleAddToStory}
              onDelete={handleDeleteEntity}
              onRefresh={refreshData}
              isSyncing={isSyncing}
              isRefreshing={isRefreshing}
            />
          </motion.div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Mode Toggle / Actions */}
              {mode === 'edit' ? (
                <div className="flex items-center gap-2 animate-in fade-in zoom-in duration-200">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMode('preview')}
                    className="h-9 px-4 text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveChanges}
                    disabled={isSaving}
                    className="h-9 px-4 min-w-[120px] shadow-sm"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMode('edit')}
                  className="h-9 gap-2 bg-background/50 backdrop-blur-sm border-border/60 text-muted-foreground hover:text-foreground rounded-full"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                  <span>Edit Properties</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs & Content */}
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className="flex items-center gap-1 border-b border-border/40 pb-px mb-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={cn(
                "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
                activeTab === 'overview'
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={cn(
                "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
                activeTab === 'notes'
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              Notes & Lore
            </button>
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="min-h-[500px]"
          >
            {activeTab === 'overview' ? (
              <div className="space-y-6">
                {templateData && (
                  <EntityFieldsTab
                    templateData={templateData}
                    mode={mode}
                    formValues={formValues}
                    isSaving={isSaving}
                    onModeChange={setMode}
                    onSaveChanges={handleSaveChanges}
                    onInputChange={handleInputChange}
                    onRefreshData={handleSyncFields}
                    layout="full"
                  />
                )}
              </div>
            ) : (
              <div>
                {entity && <EntityNotesTab entityId={entity.id} />}
              </div>
            )}
          </motion.div>
        </div>


      </main>
    </div>
  );
}

export default EntityDetailsPage;